import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Share2, Eye, Upload, Music2, Volume2, VolumeX, Play, Instagram } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FollowButton from "../components/FollowButton";
import { stopAllMedia } from "../hooks/useAudioPlayer";
import { isLiked, toggleLike } from "../lib/userPrefs";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function VideoCard({ video, onLike }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasViewed, setHasViewed] = useState(false);
  const [liked, setLiked] = useState(() => isLiked("videos", video.id));

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Global handler: quando ESTE vídeo começa a tocar com som, pausa os OUTROS vídeos da página
    const onPlay = () => {
      if (el.muted) return; // silencioso = ok, é só preview de viewport
      const allVideos = document.querySelectorAll("video");
      allVideos.forEach((other) => {
        if (other !== el) {
          other.pause();
        }
      });
    };
    el.addEventListener("play", onPlay);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.intersectionRatio > 0.6) {
            // Auto-play silencioso quando entra no viewport
            if (el.muted) {
              // Preload="none" → garante que o vídeo está carregado antes do play
              if (el.readyState === 0) {
                try { el.load(); } catch {}
              }
              el.play().catch(() => {});
              setIsPlaying(true);
            }
            if (!hasViewed) {
              fetch(`${API}/api/video-submissions/${video.id}/view`, { method: "POST" }).catch(() => {});
              setHasViewed(true);
            }
          } else {
            el.pause();
            setIsPlaying(false);
            if (!el.muted) {
              el.muted = true;
              setIsMuted(true);
            }
          }
        });
      },
      { threshold: [0.6] }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      el.removeEventListener("play", onPlay);
    };
  }, [video.id, hasViewed]);

  const muteAllOthers = () => {
    const all = document.querySelectorAll("video");
    all.forEach((other) => {
      if (other !== videoRef.current) {
        other.pause();
        other.muted = true;
      }
    });
  };

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused || el.muted) {
      try { stopAllMedia(); } catch {}
      muteAllOthers();
      if (el.readyState === 0) {
        try { el.load(); } catch {}
      }
      el.muted = false;
      setIsMuted(false);
      el.play().catch(() => {});
      setIsPlaying(true);
    } else {
      el.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.muted) {
      try { stopAllMedia(); } catch {}
      muteAllOthers();
      el.muted = false;
      setIsMuted(false);
      if (el.paused) {
        el.play().catch(() => {});
        setIsPlaying(true);
      }
    } else {
      el.muted = true;
      setIsMuted(true);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/video/${video.id}`;
    const text = `${video.artist_name} — ${video.title}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch {
      toast.info(url);
    }
  };

  const fullSrc = video.video_url?.startsWith("http") ? video.video_url : `${API}${video.video_url}`;

  return (
    <div
      className="relative bg-black rounded-2xl overflow-hidden border border-white/5 shadow-2xl hover:border-[#FF007F]/40 transition-all"
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-[9/16] bg-zinc-900">
        <video
          ref={videoRef}
          src={fullSrc}
          poster={video.thumbnail_url || undefined}
          className="w-full h-full object-cover"
          loop
          playsInline
          muted={isMuted}
          preload="none"
          onClick={togglePlay}
        />
        {/* Tap overlay for play state */}
        {!isPlaying && (
          <button
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/30"
            aria-label="Tocar"
          >
            <div className="w-16 h-16 rounded-full bg-[#FF007F] flex items-center justify-center shadow-2xl">
              <Play size={28} fill="white" className="ml-1 text-white" />
            </div>
          </button>
        )}

        {/* Top gradient + mute button */}
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
        <button
          onClick={toggleMute}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center hover:bg-black/60 transition"
          aria-label={isMuted ? "Ativar som" : "Mutar"}
          data-testid={`video-mute-${video.id}`}
        >
          {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>

        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
          <p className="text-[#FF007F] font-bold text-xs uppercase tracking-wider mb-1 truncate">
            {video.artist_name}
          </p>
          <h3 className="font-bold text-white text-sm leading-snug line-clamp-2 mb-3">{video.title}</h3>
          <div className="flex items-center gap-3 text-xs text-zinc-300">
            <span className="flex items-center gap-1"><Eye size={12} /> {video.views || 0}</span>
            <span className="flex items-center gap-1"><Heart size={12} /> {video.likes || 0}</span>
          </div>
        </div>
      </div>

      {/* Side actions (desktop hover) / Bottom row (always visible) */}
      <div className="flex items-center justify-around py-3 border-t border-white/5">
        <button
          onClick={() => {
            if (liked) {
              toast.info("Você já curtiu");
              return;
            }
            const now = toggleLike("videos", video.id);
            setLiked(now);
            if (now) onLike(video.id);
          }}
          className={`flex items-center gap-1.5 transition text-xs font-bold ${
            liked ? "text-[#FF007F]" : "text-zinc-400 hover:text-[#FF007F]"
          }`}
          data-testid={`video-like-${video.id}`}
        >
          <Heart size={18} fill={liked ? "#FF007F" : "none"} />
          {liked ? "Curtido" : "Curtir"}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 text-zinc-400 hover:text-emerald-400 transition text-xs font-bold"
          data-testid={`video-share-${video.id}`}
        >
          <Share2 size={18} /> Compartilhar
        </button>
        {video.instagram_source_url && (
          <a
            href={video.instagram_source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-zinc-400 hover:text-pink-400 transition text-xs font-bold"
          >
            <Instagram size={18} /> Original
          </a>
        )}
      </div>
    </div>
  );
}

export default function VitrinePage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 8;

  const loadPage = async (pageNum) => {
    try {
      // Client-side timeout: se o servidor demora >12s, aborta e mostra erro
      // (evita tela em branco silenciosa em conexões lentas / API instável)
      const ctrl = new AbortController();
      const timeoutId = setTimeout(() => ctrl.abort(), 12000);
      const r = await fetch(
        `${API}/api/video-submissions/public?limit=${PAGE_SIZE}&skip=${pageNum * PAGE_SIZE}`,
        { signal: ctrl.signal }
      );
      clearTimeout(timeoutId);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const arr = Array.isArray(data) ? data : [];
      setVideos((prev) => (pageNum === 0 ? arr : [...prev, ...arr]));
      if (arr.length < PAGE_SIZE) setHasMore(false);
      if (pageNum === 0) setLoadError(false);
    } catch (e) {
      setHasMore(false);
      if (pageNum === 0) setLoadError(true);
    }
  };

  const retryLoad = () => {
    setLoading(true);
    setLoadError(false);
    setVideos([]);
    setPage(0);
    setHasMore(true);
    loadPage(0).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPage(0).finally(() => setLoading(false));
  }, []);

  // IntersectionObserver no sentinel: carrega próxima página quando aproxima do final
  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          setLoadingMore(true);
          const next = page + 1;
          loadPage(next).finally(() => {
            setPage(next);
            setLoadingMore(false);
          });
        }
      },
      { rootMargin: "400px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [page, hasMore, loadingMore, loading]);

  const handleLike = async (id) => {
    try {
      const r = await fetch(`${API}/api/video-submissions/${id}/like`, { method: "POST" });
      const d = await r.json();
      setVideos((prev) => prev.map((v) => (v.id === id ? { ...v, likes: d.likes } : v)));
      toast.success("❤️ Curtido!");
    } catch {
      toast.error("Erro ao curtir");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      <div className="relative overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF007F]/15 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/30 text-[#FF007F] text-xs font-bold mb-4">
                <Instagram size={14} /> VITRINE DOS ARTISTAS
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mb-3">
                Vitrine do <span className="text-[#FF007F] italic">Arrocha</span>.
              </h1>
              <p className="text-zinc-400 text-lg max-w-2xl">
                Vídeos curtos dos artistas — shows, ensaios, prévias. Estilo Reels, direto da cena Arrocha.
              </p>
            </div>
            <Link
              to="/envie-seu-video"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition-all whitespace-nowrap shadow-lg shadow-[#FF007F]/20"
              data-testid="send-video-cta"
            >
              <Upload size={18} /> Envie seu Vídeo
            </Link>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin" data-testid="vitrine-loading" />
          </div>
        ) : loadError ? (
          <div className="text-center py-20" data-testid="vitrine-error">
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 mx-auto mb-4 flex items-center justify-center">
              <Music2 size={28} className="text-red-400" />
            </div>
            <p className="text-white font-bold mb-1">Ops! Não conseguimos carregar a Vitrine.</p>
            <p className="text-zinc-500 text-sm mb-6">Verifique sua conexão e tente novamente.</p>
            <button
              onClick={retryLoad}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition"
              data-testid="vitrine-retry"
            >
              Tentar novamente
            </button>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20" data-testid="vitrine-empty">
            <Music2 size={48} className="mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-500 mb-6">A Vitrine está vazia. Seja o primeiro a postar!</p>
            <Link
              to="/envie-seu-video"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition"
            >
              <Upload size={18} /> Envie seu Vídeo
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {videos.map((v) => (
                <VideoCard key={v.id} video={v} onLike={handleLike} />
              ))}
            </div>
            {/* Sentinel + loading indicator */}
            {hasMore && (
              <div
                ref={sentinelRef}
                className="flex justify-center py-10"
                data-testid="vitrine-sentinel"
              >
                {loadingMore && (
                  <div className="w-8 h-8 border-2 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
                )}
              </div>
            )}
            {!hasMore && videos.length > 0 && (
              <p className="text-center text-zinc-600 text-sm py-10">
                Você chegou ao fim — {videos.length} vídeos.
              </p>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
